import { supabase } from '../lib/supabase';
import type { UserRole } from '../types';

export interface CreateInvitationParams {
  email: string;
  organizationId: string;
  role: UserRole;
  firstName?: string | null;
  lastName?: string | null;
  companyName?: string | null;
}

export interface AcceptInvitationParams {
  token: string;
  password: string;
  firstName?: string | null;
  lastName?: string | null;
  companyName?: string | null;
}

export async function createInvitation(params: CreateInvitationParams) {
  console.log('Creating invitation with params:', JSON.stringify(params, null, 2));
  
  const { data, error } = await supabase.rpc('create_invitation', {
    p_email: params.email,
    p_organization_id: params.organizationId,
    p_role: params.role,
    p_first_name: params.firstName || null,
    p_last_name: params.lastName || null,
    p_company_name: params.companyName || null
  });

  if (error) {
    console.error('Error creating invitation:', error);
    throw error;
  }
  
  console.log('Invitation created successfully:', data);
  return data;
}

export async function acceptInvitation(params: AcceptInvitationParams) {
  console.log('Accepting invitation with params:', JSON.stringify({
    token: params.token,
    firstName: params.firstName,
    lastName: params.lastName,
    companyName: params.companyName,
    // Don't log password
    hasPassword: !!params.password
  }, null, 2));

  const { data, error } = await supabase.rpc('accept_invitation', {
    p_token: params.token,
    p_password: params.password,
    p_first_name: params.firstName,
    p_last_name: params.lastName,
    p_company_name: params.companyName
  });

  if (error) {
    console.error('Error accepting invitation:', error);
    throw error;
  }
  
  console.log('Invitation accepted successfully');
  return data;
}

export async function getInvitationByToken(token: string) {
  console.log('Getting invitation by token:', token);
  
  const { data, error } = await supabase
    .from('invitations')
    .select(`
      *,
      organizations (
        name
      )
    `)
    .eq('token', token)
    .eq('status', 'pending')
    .gt('expires_at', new Date().toISOString())
    .single();

  if (error) {
    console.error('Error getting invitation by token:', error);
    throw error;
  }
  
  console.log('Invitation found:', data);
  return data;
}

export async function getOrganizationInvitations(organizationId: string) {
  const { data, error } = await supabase
    .from('invitations')
    .select('*')
    .eq('organization_id', organizationId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
}

export async function expireOldInvitations() {
  const { error } = await supabase.rpc('expire_old_invitations');
  if (error) throw error;
}

export async function sendInvitationEmail(params: {
  invitationId: string;
  token: string;
  organizationName: string;
  adminName?: string;
  recipientEmail: string;
}) {
  // Log the parameters to help with debugging
  console.log('Sending invitation with params:', JSON.stringify(params));
  
  // Validate required parameters
  if (!params.invitationId || !params.token || !params.organizationName || !params.recipientEmail) {
    throw new Error('Missing required parameters');
  }

  const response = await fetch(`${supabase.supabaseUrl}/functions/v1/send-invitation-email`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${supabase.supabaseKey}`
    },
    body: JSON.stringify(params)
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Failed to send invitation email');
  }

  return await response.json();
}